import React from "react";
import { Pencil } from "lucide-react";
import {
  AppButton, AppCard, AppCardContent, AppCardHeader, AppCardTitle, Callout,
} from "../../ui";
import SectionMark from "./SectionMark";
import { marksText, minutesText, sectionLine } from "./PaperSummary";
import { isPublicOnly, needsSections, paperSummary } from "./builderRules";

const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/** A card on the review screen, with a way back to the step that sets it. */
function ReviewCard({ title, onChange, children }) {
  return (
    <AppCard>
      <AppCardHeader className="tw-flex-row tw-flex-wrap tw-items-center tw-justify-between tw-gap-3">
        <AppCardTitle>{title}</AppCardTitle>
        {onChange && (
          <AppButton type="button" size="sm" variant="ghost" onClick={onChange}>
            <Pencil size={16} aria-hidden="true" /> Change
          </AppButton>
        )}
      </AppCardHeader>
      <AppCardContent>{children}</AppCardContent>
    </AppCard>
  );
}

const namesOf = (ids, list, labelOf) => {
  const byId = new Map(list.map((x) => [String(x._id), x]));
  const names = ids.map((id) => byId.get(String(id))).filter(Boolean).map(labelOf);
  if (names.length <= 4) return names.join(", ");
  return `${names.slice(0, 4).join(", ")} and ${names.length - 4} more`;
};

const when = (v) => (v
  ? new Date(v).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit" })
  : null);

function rulesSentences(form, totals) {
  const publicOnly = isPublicOnly(form);
  const lines = [];
  lines.push(totals.timedSections
    ? `Sections run in order, ${totals.minutes} minutes in all — each on its own timer.`
    : totals.minutes ? `${totals.minutes} minutes in total.` : "No time limit.");
  lines.push(Number(form.passingPercentage) > 0 ? `Pass mark ${form.passingPercentage}%.` : "No pass mark.");
  if (!publicOnly) {
    lines.push(Number(form.maxAttempts) > 0 ? `${plural(Number(form.maxAttempts), "attempt")} allowed.` : "Unlimited attempts.");
  }
  if (form.negativeMarking) lines.push(`A wrong answer loses ${form.negativeMarkValue} marks.`);
  if (form.partialMarking) lines.push("Partial credit on multiple-answer questions.");
  const order = [form.shuffleQuestions && "questions", form.shuffleOptions && "options"].filter(Boolean);
  if (order.length) lines.push(`Shuffled: ${order.join(" and ")}.`);
  // Attempts, results and security are the public exam's to decide.
  if (publicOnly) return lines;
  lines.push(form.allowReview
    ? `Students review their answers${form.showCorrectAnswers ? ", with the correct ones" : ""}${form.showExplanation ? " and explanations" : ""}.`
    : "Students see their score only — no review.");
  const guard = [
    form.antiCheat.tabSwitchDetection && `tab switches watched (${form.antiCheat.maxTabSwitches} allowed${form.antiCheat.autoSubmitOnViolation ? ", then submitted" : ""})`,
    form.antiCheat.fullscreenRequired && "full screen",
    form.antiCheat.disableCopyPaste && "no copy and paste",
    form.antiCheat.disableRightClick && "no right-click",
  ].filter(Boolean);
  lines.push(guard.length ? `Security: ${guard.join(", ")}.` : "No exam security rules.");
  return lines;
}

function audienceSentence(form, { batches, courses, studentLabels }) {
  switch (form.assignmentType) {
    case "batch": return form.assignedBatches.length
      ? `Batches: ${namesOf(form.assignedBatches, batches, (b) => b.name)}.` : "No batch chosen yet.";
    case "course": return form.assignedCourses.length
      ? `Courses: ${namesOf(form.assignedCourses, courses, (c) => c.title || c.name)}.` : "No course chosen yet.";
    case "direct": return form.assignedStudents.length
      ? `${plural(form.assignedStudents.length, "student")}: ${form.assignedStudents.slice(0, 4).map((id) => studentLabels[id] || "Student").join(", ")}${form.assignedStudents.length > 4 ? " and more" : ""}.`
      : "No student chosen yet.";
    case "open": return "Every student in the institute.";
    case "rules": return `${plural(form.assignmentRules.filter((r) => r.target).length, "group")}, each with its own rules.`;
    default: return "";
  }
}

/**
 * Step 6 — everything on one screen before it reaches students. What blocks
 * publishing is listed first, each with a way to the step that fixes it.
 */
export default function ReviewStep({
  form, problems, warnings, onGoTo, isLibrary, batches, courses, studentLabels, status, onGoToUsage = "details",
}) {
  const { sections, totals } = paperSummary(form);
  const sectioned = needsSections(form) && sections.length > 1;
  const open = when(form.startDate);
  const close = when(form.endDate);
  const publicOnly = !isLibrary && isPublicOnly(form);
  const ready = status === "published"
    ? (publicOnly
      ? "Everything is in place. Saving updates it for every public exam that uses it."
      : "Everything is in place. Saving updates it for students straight away.")
    : (publicOnly
      ? "Everything is in place. Save it, then set up the public exam — the link, the dates, the sign-up form and the security."
      : "Everything is in place. Publish to give it to students now, or save it as a draft and publish later.");

  return (
    <div className="tw-flex tw-flex-col tw-gap-4">
      {problems.length > 0 ? (
        <Callout tone="danger" role="alert">
          <span className="tw-mb-1.5 tw-block tw-font-semibold">
            Fix {problems.length === 1 ? "this" : `these ${problems.length}`} before publishing
          </span>
          <ul className="tw-m-0 tw-flex tw-list-none tw-flex-col tw-gap-1 tw-p-0">
            {problems.map((p) => (
              <li key={`${p.step}-${p.message}`} className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
                <span>{p.message}</span>
                <AppButton type="button" size="xs" variant="outline" onClick={() => onGoTo(p.step)}>Fix</AppButton>
              </li>
            ))}
          </ul>
        </Callout>
      ) : (
        <Callout tone="success">{ready}</Callout>
      )}
      {warnings.length > 0 && (
        <Callout tone="warning">
          <ul className="tw-m-0 tw-flex tw-list-none tw-flex-col tw-gap-1 tw-p-0">
            {warnings.map((w) => <li key={w}>{w}</li>)}
          </ul>
        </Callout>
      )}

      <ReviewCard title={form.title.trim() || "Untitled assessment"} onChange={() => onGoTo("details")}>
        <div className="tw-flex tw-flex-col tw-gap-1 tw-text-[0.8125rem]">
          <span className="tw-text-content-muted">{form.type === "exam" ? "Exam" : "Practice"}</span>
          {form.description && <span className="tw-text-content">{form.description}</span>}
        </div>
      </ReviewCard>

      <ReviewCard title="The paper" onChange={() => onGoTo("questions")}>
        <div className="tw-flex tw-flex-col tw-gap-4">
          <div className="tw-grid tw-grid-cols-3 tw-gap-3">
            {[
              ["Questions", totals.coding ? `${totals.questions} + ${totals.coding} coding` : String(totals.questions)],
              ["Marks", marksText(totals)],
              ["Time", minutesText(totals)],
            ].map(([k, v]) => (
              <div key={k} className="tw-flex tw-flex-col tw-gap-0.5 tw-rounded-md tw-border tw-border-solid tw-border-line tw-bg-surface tw-px-3 tw-py-2.5">
                <span className="tw-text-xs tw-text-content-muted">{k}</span>
                <span className="tw-text-[0.9375rem] tw-font-semibold tw-tabular-nums tw-text-content">{v}</span>
              </div>
            ))}
          </div>
          {sectioned && (
            <ol className="tw-m-0 tw-flex tw-list-none tw-flex-col tw-p-0">
              {sections.map((s) => (
                <li key={s.key} className="tw-flex tw-items-center tw-gap-3 tw-py-2.5 tw-border-0 tw-border-t tw-border-solid tw-border-line first:tw-border-t-0">
                  <SectionMark letter={s.letter} size="sm" />
                  <span className="tw-min-w-0 tw-flex-1 tw-text-sm tw-font-medium tw-text-content">{s.title}</span>
                  <span className={s.empty ? "tw-text-[0.8125rem] tw-text-danger" : "tw-text-[0.8125rem] tw-text-content-muted"}>
                    {sectionLine(s)}
                    {s.marks != null && !s.empty ? ` · ${s.marks} marks` : ""}
                    {s.minutes ? ` · ${s.minutes} min` : ""}
                  </span>
                </li>
              ))}
            </ol>
          )}
        </div>
      </ReviewCard>

      <ReviewCard title={publicOnly ? "Time and marks" : "Rules"} onChange={() => onGoTo("rules")}>
        <ul className="tw-m-0 tw-flex tw-list-disc tw-flex-col tw-gap-1 tw-pl-5 tw-text-[0.8125rem] tw-text-content">
          {rulesSentences(form, totals).map((l) => <li key={l}>{l}</li>)}
        </ul>
      </ReviewCard>

      {publicOnly ? (
        <ReviewCard title="Who takes it" onChange={() => onGoTo(onGoToUsage)}>
          <ul className="tw-m-0 tw-flex tw-list-disc tw-flex-col tw-gap-1 tw-pl-5 tw-text-[0.8125rem] tw-text-content">
            <li>Anyone you let in through a public exam link. Your students won't see it, and nobody is notified.</li>
            <li>The link, the dates, who can enter and the security are set on the public exam.</li>
          </ul>
        </ReviewCard>
      ) : (
        <ReviewCard title={isLibrary ? "Pricing" : "Students and dates"} onChange={() => onGoTo("audience")}>
          {isLibrary ? (
            <p className="tw-m-0 tw-text-[0.8125rem] tw-text-content">
              {form.pricing.model === "paid"
                ? `Paid — ${form.pricing.currency} ${form.pricing.amount}${form.pricing.period ? ` ${form.pricing.period}` : ""}.`
                : "Free to import."}
            </p>
          ) : (
            <ul className="tw-m-0 tw-flex tw-list-disc tw-flex-col tw-gap-1 tw-pl-5 tw-text-[0.8125rem] tw-text-content">
              <li>{audienceSentence(form, { batches, courses, studentLabels })}</li>
              <li>
                {open && close ? `Open from ${open} until ${close}.`
                  : open ? `Opens ${open}.` : close ? `Open now, until ${close}.` : "Open as soon as it is published."}
              </li>
            </ul>
          )}
        </ReviewCard>
      )}
    </div>
  );
}
